import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListPricelistsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-56 Pricelists list.
 *
 * The pricelist editor was reachable at /pricelists/:id and nothing listed
 * them, so a pricelist was found again by remembering its id.
 *
 * Expired is its own column rather than a strikethrough on the dates. A
 * pricelist whose validity has passed still prices nothing, and reading
 * that off two dates is arithmetic somebody has to do in their head.
 */

type Row = ListPricelistsResult['pricelists'][number];

function PricelistsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'name', direction: 'asc' });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListPricelistsResult, ApiError>({
    queryKey: ['pricelists'],
    queryFn: () => api.listPricelists({}),
  });

  /*
   * Narrowed and ordered here rather than by the server.
   *
   * A workspace has tens of these, not tens of thousands. Paging them would
   * mean a cursor, a total and a page size describing a list that fits on one
   * screen - and the toolbar's search is honest about what it does: it
   * narrows what is already loaded.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.pricelists ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => sort.field === 'currency'
        ? a.currency.localeCompare(b.currency) * direction
        : a.name.localeCompare(b.name) * direction);
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'currency',
        header: t('list.column.currency'),
        sortField: 'currency',
        essential: true,
        cell: (row) => row.currency,
      },
      {
        key: 'rules',
        header: t('list.column.rules'),
        align: 'end',
        cell: (row) => <Num align="end" className="text-ink">{formatCount(row.rules, format)}</Num>,
      },
      {
        key: 'validity',
        header: t('list.column.validity'),
        hideOnCard: true,
        cell: (row) => (
          <span className={row.expired ? 'text-warning-text' : 'text-ink-meta'}>
            {row.expired ? t('list.expired') : t('list.active')}
          </span>
        ),
      },
    ],
    [t, format],
  );

  return (
    <RecordList<Row>
      title={t('list.pricelists.title')}
      icon="Tag"
      rowId={(row) => row.pricelistId}
      recordHref={(row) => `/pricelists/${row.pricelistId}`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.pricelists.empty.title')}
      emptyBody={t('list.pricelists.empty.body')}
      emptyIllustration="Tag"
    />
  );
}

function PricelistsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Tag"
      title={t('list.pricelists.empty.title')}
      body={t('list.pricelists.empty.body')}
      secondary={{ label: t('nav.setup'), href: '/setup' }}
    />
  );
}

function PricelistsListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function PricelistsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Pricelists could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-56',
  route: '/pricelists',
  surface: 'page',
  title: 'Pricelists list',
  features: ['F-162'],
  entitlement: 'F-162',
  reachedBy: [{ kind: 'nav', item: 'pricelists' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: PricelistsListEmpty,
    loading: PricelistsListLoading,
    error: PricelistsListError,
  },
  component: PricelistsListScreen,
});

export default screen;
